import React, { useEffect, useState, useRef, Children, Component } from 'react';
import { motion, useInView, useMotionValue, useTransform, animate } from 'framer-motion';
import { ArrowRightIcon, BarChart3Icon, FileTextIcon, GlobeIcon, ShieldCheckIcon, ScaleIcon, ScanLineIcon, LayoutDashboardIcon, FolderIcon, SettingsIcon, CheckCircle2Icon, ClockIcon, FactoryIcon, CpuIcon, DownloadIcon, FlameIcon, RadioTowerIcon, LandmarkIcon, PackageIcon, HardHatIcon, WheatIcon, PillIcon, PlayIcon, TrendingDownIcon, ZapIcon, DatabaseIcon } from 'lucide-react';
import { Footer } from '../components/Footer';
import { HowCarbonSafeWorks } from '../components/HowCarbonSafeWorks';
import { MAILTO } from '../utils/mailto';
/* ═══════════════════════════════════════════════
   HERO IMAGE COMPOSITION
   Layered uploaded images with depth
   ═══════════════════════════════════════════════ */
function HeroImageComposition() {
  return <div className="relative w-full min-h-[480px] lg:min-h-[520px]" data-id="element-2300">
      {/* Main: Corporate team photo */}
      <motion.div className="relative z-10 rounded-2xl overflow-hidden shadow-2xl" initial={{
      opacity: 0,
      y: 30
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.8,
      delay: 0.2,
      ease: [0.22, 1, 0.36, 1]
    }} data-id="element-2301">

        <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/3zho3j7rbfxjzyaqujg3yf/eabcd6cc-4563-497d-b5b9-3dca6804fbf0/68820e57-4d9e-40a6-b7b3-9ac66568bb2d.png" alt="Corporate team reviewing carbon data" className="w-full h-auto rounded-2xl" data-id="element-2302" />

      </motion.div>

      {/* Overlapping: Net Zero Tracker card */}
      <motion.div className="absolute -bottom-8 -left-6 z-20 w-[55%] max-w-[280px]" initial={{
      opacity: 0,
      y: 40,
      x: -20
    }} animate={{
      opacity: 1,
      y: 0,
      x: 0
    }} transition={{
      duration: 0.8,
      delay: 0.5,
      ease: [0.22, 1, 0.36, 1]
    }} data-id="element-2303">

        <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/3zho3j7rbfxjzyaqujg3yf/eabcd6cc-4563-497d-b5b9-3dca6804fbf0/pasted-image.png" alt="Net Zero Tracker showing Scope 1, 2, 3 emissions" className="w-full h-auto rounded-2xl shadow-2xl border border-[#9aa6af]" data-id="element-2304" />

      </motion.div>

      {/* Overlapping: Emissions Dashboard card */}
      <motion.div className="absolute -top-6 -right-6 z-20 w-[50%] max-w-[260px]" initial={{
      opacity: 0,
      y: -20,
      x: 20
    }} animate={{
      opacity: 1,
      y: 0,
      x: 0
    }} transition={{
      duration: 0.8,
      delay: 0.7,
      ease: [0.22, 1, 0.36, 1]
    }} data-id="element-2305">

        <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/3zho3j7rbfxjzyaqujg3yf/eabcd6cc-4563-497d-b5b9-3dca6804fbf0/ghg-emissions-dashboard.png" alt="Emissions Dashboard with scope breakdown" className="w-full h-auto rounded-2xl shadow-2xl border border-[#9aa6af]" data-id="element-2306" />

      </motion.div>
    </div>;
}
/* ═══════════════════════════════════════════════
   ANIMATED STAT
   ═══════════════════════════════════════════════ */
function AnimatedStat({
  value,
  label,
  detail
}: {
  value: string;
  label: string;
  detail: string;
}) {
  const numericMatch = value.match(/^([\d.]+)(.*)$/);
  const numericValue = numericMatch ? parseFloat(numericMatch[1]) : 0;
  const suffix = numericMatch ? numericMatch[2] : value;
  const isNumeric = !!numericMatch;
  const [display, setDisplay] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-40px'
  });
  const mv = useMotionValue(0);
  const rounded = useTransform(mv, v => {
    if (numericValue < 10) return Number(v.toFixed(1));
    return Math.round(v);
  });
  useEffect(() => {
    if (isInView && isNumeric) {
      const controls = animate(mv, numericValue, {
        duration: 2,
        ease: [0.22, 1, 0.36, 1]
      });
      return controls.stop;
    }
  }, [isInView, numericValue, mv, isNumeric]);
  useEffect(() => {
    const unsub = rounded.on('change', v => setDisplay(v));
    return unsub;
  }, [rounded]);
  return <div ref={ref} className="text-center" data-id="element-2307">
      <p className="text-4xl sm:text-5xl font-semibold text-carbon-bright" data-id="element-2308">
        {isNumeric ? `${display.toLocaleString()}${suffix}` : value}
      </p>
      <p className="mt-2 text-base font-bold text-white" data-id="element-2309">{label}</p>
      <p className="mt-1 text-sm text-white/50 leading-relaxed" data-id="element-2310">{detail}</p>
    </div>;
}
/* ═══════════════════════════════════════════════
   SCOPE BREAKDOWN CHART
   ═══════════════════════════════════════════════ */
function ScopeBreakdownChart() {
  return <div className="bg-white rounded-2xl border border-[#9aa6af] p-6 sm:p-8 shadow-lg" data-id="element-2311">
      <div className="mb-6" data-id="element-2312">
        <p className="text-xs font-bold uppercase tracking-wider text-gray-400" data-id="element-2313">
          Typical Enterprise Profile
        </p>
        <p className="text-lg font-bold text-carbon-deep mt-0.5" data-id="element-2314">
          GHG Emissions by Scope
        </p>
      </div>

      <div className="space-y-5" data-id="element-2315">
        <div data-id="element-2316">
          <div className="flex items-center justify-between mb-2" data-id="element-2317">
            <div className="flex items-center gap-2" data-id="element-2318">
              <div className="w-3 h-3 rounded-sm bg-carbon-deep" data-id="element-2319" />
              <span className="text-sm font-bold text-carbon-deep" data-id="element-2320">
                Scope 1
              </span>
            </div>
            <span className="text-sm font-bold text-carbon-deep" data-id="element-2321">18%</span>
          </div>
          <div className="h-3 bg-gray-100 rounded-full overflow-hidden" data-id="element-2322">
            <motion.div className="h-full rounded-full bg-carbon-deep" initial={{
            width: 0
          }} whileInView={{
            width: '18%'
          }} viewport={{
            once: true
          }} transition={{
            duration: 1.2,
            delay: 0.3,
            ease: [0.22, 1, 0.36, 1]
          }} data-id="element-2323" />

          </div>
          <p className="text-xs text-gray-400 mt-1.5" data-id="element-2324">
            Direct emissions from owned facilities
          </p>
        </div>

        <div data-id="element-2325">
          <div className="flex items-center justify-between mb-2" data-id="element-2326">
            <div className="flex items-center gap-2" data-id="element-2327">
              <div className="w-3 h-3 rounded-sm bg-carbon-mid" data-id="element-2328" />
              <span className="text-sm font-bold text-carbon-deep" data-id="element-2329">
                Scope 2
              </span>
            </div>
            <span className="text-sm font-bold text-carbon-deep" data-id="element-2330">32%</span>
          </div>
          <div className="h-3 bg-gray-100 rounded-full overflow-hidden" data-id="element-2331">
            <motion.div className="h-full rounded-full bg-carbon-mid" initial={{
            width: 0
          }} whileInView={{
            width: '32%'
          }} viewport={{
            once: true
          }} transition={{
            duration: 1.2,
            delay: 0.4,
            ease: [0.22, 1, 0.36, 1]
          }} data-id="element-2332" />

          </div>
          <p className="text-xs text-gray-400 mt-1.5" data-id="element-2333">
            Indirect emissions from purchased energy
          </p>
        </div>

        <div data-id="element-2334">
          <div className="flex items-center justify-between mb-2" data-id="element-2335">
            <div className="flex items-center gap-2" data-id="element-2336">
              <div className="w-3 h-3 rounded-sm bg-carbon-bright" data-id="element-2337" />
              <span className="text-sm font-bold text-carbon-deep" data-id="element-2338">
                Scope 3
              </span>
            </div>
            <span className="text-sm font-bold text-carbon-deep" data-id="element-2339">50%</span>
          </div>
          <div className="h-3 bg-gray-100 rounded-full overflow-hidden" data-id="element-2340">
            <motion.div className="h-full rounded-full bg-carbon-bright" initial={{
            width: 0
          }} whileInView={{
            width: '50%'
          }} viewport={{
            once: true
          }} transition={{
            duration: 1.2,
            delay: 0.5,
            ease: [0.22, 1, 0.36, 1]
          }} data-id="element-2341" />

          </div>
          <p className="text-xs text-gray-400 mt-1.5" data-id="element-2342">
            Value chain emissions (upstream & downstream)
          </p>
        </div>
      </div>

      <div className="mt-6 flex items-center gap-3 p-3 rounded-xl bg-carbon-bright/5 border border-carbon-bright/15" data-id="element-2343">
        <div className="w-8 h-8 rounded-lg bg-carbon-bright/15 flex items-center justify-center flex-shrink-0" data-id="element-2344">
          <BarChart3Icon className="w-4 h-4 text-carbon-bright" data-id="element-2345" />
        </div>
        <p className="text-xs text-gray-500 leading-snug" data-id="element-2346">
          <span className="font-bold text-carbon-deep" data-id="element-2347">
            Scope 3 typically represents 50-80% of total emissions
          </span>
          . CarbonSafe automates calculations across all 15 GHG Protocol
          categories.
        </p>
      </div>
    </div>;
}
/* ═══════════════════════════════════════════════
   INDUSTRY CARD
   ═══════════════════════════════════════════════ */
interface IndustryInfo {
  icon: ComponentType<{
    className?: string;
  }>;
  name: string;
  color: string;
  description: string;
}
function IndustryCard({
  industry,
  index,
  isInView
}: {
  industry: IndustryInfo;
  index: number;
  isInView: boolean;
}) {
  const IconComp = industry.icon;
  return <motion.article className="relative p-5 rounded-2xl border border-[#9aa6af] bg-white overflow-hidden" initial={{
    opacity: 0,
    y: 30
  }} animate={isInView ? {
    opacity: 1,
    y: 0
  } : {}} transition={{
    duration: 0.5,
    delay: 0.08 + index * 0.05,
    ease: [0.22, 1, 0.36, 1]
  }} data-id="element-2348">

      {/* Watermark background icon */}
      <div className="absolute -bottom-4 -right-4 opacity-[0.04] pointer-events-none select-none" data-id="element-2349">
        <IconComp className="w-28 h-28" data-id="element-2350" />
      </div>

      <div className="relative z-10 flex items-start gap-3.5" data-id="element-2351">
        <div className="w-11 h-11 rounded-xl bg-carbon-bright/15 text-carbon-bright flex items-center justify-center flex-shrink-0" data-id="element-2352">
          <IconComp className="w-5 h-5" data-id="element-2353" />
        </div>
        <div className="min-w-0 flex-1" data-id="element-2354">
          <h3 className="text-sm font-bold text-carbon-deep" data-id="element-2355">
            {industry.name}
          </h3>
          <p className="text-xs text-gray-500 leading-relaxed mt-1.5" data-id="element-2356">
            {industry.description}
          </p>
        </div>
      </div>
    </motion.article>;
}
/* ═══════════════════════════════════════════════
   DASHBOARD MOCKUP (Platform Preview)
   ═══════════════════════════════════════════════ */
function GHGDashboardMockup() {
  const barHeights = [82, 75, 68, 72, 60, 55, 48];
  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  return <div className="rounded-2xl shadow-2xl border border-gray-200 overflow-hidden bg-white" style={{
    transform: 'perspective(1200px) rotateX(2deg) rotateY(-1deg) scale(0.92)'
  }} data-id="element-2357">

      {/* Browser chrome */}
      <div className="flex items-center gap-2 px-4 py-2.5 bg-gray-50 border-b border-gray-200" data-id="element-2358">
        <div className="flex gap-1.5" data-id="element-2359">
          <div className="w-3 h-3 rounded-full bg-red-400" data-id="element-2360" />
          <div className="w-3 h-3 rounded-full bg-yellow-400" data-id="element-2361" />
          <div className="w-3 h-3 rounded-full bg-green-400" data-id="element-2362" />
        </div>
        <div className="flex-1 mx-4" data-id="element-2363">
          <div className="bg-white rounded-md px-3 py-1 text-xs text-gray-400 font-mono border border-gray-200 max-w-md" data-id="element-2364">
            app.carbonsafe.io/ghg-reporting
          </div>
        </div>
      </div>

      <div className="flex min-h-[420px]" data-id="element-2365">
        {/* Sidebar */}
        <div className="w-48 bg-carbon-deep flex-shrink-0 p-3 hidden sm:block" data-id="element-2366">
        <div className="flex items-center gap-2 px-2 py-2 mb-4" data-id="element-2367">
          <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/3zho3j7rbfxjzyaqujg3yf/eabcd6cc-4563-497d-b5b9-3dca6804fbf0/carbonsafe-logo-favicon.svg" alt="CarbonSafe" className="h-6 w-auto" data-id="element-2368" />
          <span className="text-white text-[11px] font-bold tracking-widest" data-id="element-2369">
            CARBONSAFE
          </span>
        </div>
          <nav className="space-y-0.5" data-id="element-2370">
            <div className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg bg-white/10 text-white text-xs font-medium" data-id="element-2371">
              <LayoutDashboardIcon className="w-3.5 h-3.5" data-id="element-2372" /> Dashboard
            </div>
            <div className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-white/50 text-xs" data-id="element-2373">
              <BarChart3Icon className="w-3.5 h-3.5" data-id="element-2374" /> Emissions
            </div>
            <div className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-white/50 text-xs" data-id="element-2375">
              <FolderIcon className="w-3.5 h-3.5" data-id="element-2376" /> Facilities
            </div>
            <div className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-white/50 text-xs" data-id="element-2377">
              <FileTextIcon className="w-3.5 h-3.5" data-id="element-2378" /> Reports
            </div>
            <div className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-white/50 text-xs" data-id="element-2379">
              <GlobeIcon className="w-3.5 h-3.5" data-id="element-2380" /> CBAM
            </div>
            <div className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-white/50 text-xs" data-id="element-2381">
              <SettingsIcon className="w-3.5 h-3.5" data-id="element-2382" /> Settings
            </div>
          </nav>
        </div>

        {/* Main content */}
        <div className="flex-1 p-4 sm:p-5 bg-gray-50 overflow-hidden" data-id="element-2383">
          <div className="flex items-center justify-between mb-4" data-id="element-2384">
            <div data-id="element-2385">
              <h3 className="text-sm font-bold text-carbon-deep" data-id="element-2386">
                GHG Inventory | FY 2026
              </h3>
              <p className="text-[11px] text-gray-400 mt-0.5" data-id="element-2387">
                Last updated 8 min ago
              </p>
            </div>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold" data-id="element-2388">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" data-id="element-2389" /> CSRD
              Compliant
            </span>
          </div>

          {/* Stat cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 mb-4" data-id="element-2390">
            <div className="bg-white rounded-lg p-3 border border-[#9aa6af]" data-id="element-2391">
              <p className="text-[10px] text-gray-400 font-medium" data-id="element-2392">
                Total Emissions
              </p>
              <p className="text-lg font-bold text-carbon-deep mt-0.5" data-id="element-2393">
                12,847{' '}
                <span className="text-[10px] font-medium text-gray-400" data-id="element-2394">
                  tCO₂e
                </span>
              </p>
            </div>
            <div className="bg-white rounded-lg p-3 border border-[#9aa6af]" data-id="element-2395">
              <p className="text-[10px] text-gray-400 font-medium" data-id="element-2396">Scope 1</p>
              <p className="text-lg font-bold text-carbon-deep mt-0.5" data-id="element-2397">2,312</p>
            </div>
            <div className="bg-white rounded-lg p-3 border border-[#9aa6af]" data-id="element-2398">
              <p className="text-[10px] text-gray-400 font-medium" data-id="element-2399">Scope 2</p>
              <p className="text-lg font-bold text-carbon-deep mt-0.5" data-id="element-2400">4,180</p>
            </div>
            <div className="bg-white rounded-lg p-3 border border-[#9aa6af]" data-id="element-2401">
              <p className="text-[10px] text-gray-400 font-medium" data-id="element-2402">Scope 3</p>
              <p className="text-lg font-bold text-carbon-bright mt-0.5" data-id="element-2403">
                6,355
              </p>
            </div>
          </div>

          {/* Charts row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-2.5 mb-4" data-id="element-2404">
            <div className="bg-white rounded-lg p-3 border border-[#9aa6af]" data-id="element-2405">
              <div className="flex items-center justify-between mb-3" data-id="element-2406">
                <p className="text-[11px] font-semibold text-carbon-deep" data-id="element-2407">
                  Monthly Emissions Trend
                </p>
                <p className="text-[10px] text-gray-400" data-id="element-2408">This week</p>
              </div>
              <div className="flex items-end gap-2 h-24" data-id="element-2409">
                {barHeights.map((h, i) => <div key={i} className="flex-1 flex flex-col items-center gap-1" data-id="element-2410">

                    <div className="w-full rounded-t-sm bg-carbon-bright/80" style={{
                  height: `${h}%`
                }} data-id="element-2411" />

                    <span className="text-[9px] text-gray-400" data-id="element-2412">{days[i]}</span>
                  </div>)}
              </div>
            </div>
            <div className="bg-white rounded-lg p-3 border border-[#9aa6af]" data-id="element-2413">
              <div className="flex items-center justify-between mb-3" data-id="element-2414">
                <p className="text-[11px] font-semibold text-carbon-deep" data-id="element-2415">
                  By Scope
                </p>
                <p className="text-[10px] text-gray-400" data-id="element-2416">FY 2026</p>
              </div>
              <div className="space-y-2.5" data-id="element-2417">
                <div data-id="element-2418">
                  <div className="flex justify-between text-[10px] mb-1" data-id="element-2419">
                    <span className="text-gray-500 font-medium" data-id="element-2420">
                      Scope 1 — Direct
                    </span>
                    <span className="font-bold text-carbon-deep" data-id="element-2421">18%</span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-2422">
                    <div className="h-full bg-carbon-deep rounded-full" style={{
                    width: '18%'
                  }} data-id="element-2423" />

                  </div>
                </div>
                <div data-id="element-2424">
                  <div className="flex justify-between text-[10px] mb-1" data-id="element-2425">
                    <span className="text-gray-500 font-medium" data-id="element-2426">
                      Scope 2 — Energy
                    </span>
                    <span className="font-bold text-carbon-deep" data-id="element-2427">32%</span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-2428">
                    <div className="h-full bg-carbon-mid rounded-full" style={{
                    width: '32%'
                  }} data-id="element-2429" />

                  </div>
                </div>
                <div data-id="element-2430">
                  <div className="flex justify-between text-[10px] mb-1" data-id="element-2431">
                    <span className="text-gray-500 font-medium" data-id="element-2432">
                      Scope 3 — Value Chain
                    </span>
                    <span className="font-bold text-carbon-deep" data-id="element-2433">50%</span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-2434">
                    <div className="h-full bg-carbon-bright rounded-full" style={{
                    width: '50%'
                  }} data-id="element-2435" />

                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Activity feed */}
          <div className="bg-white rounded-lg border border-[#9aa6af] overflow-hidden" data-id="element-2436">
            <div className="px-3 py-2 border-b border-[#9aa6af]" data-id="element-2437">
              <p className="text-[11px] font-semibold text-carbon-deep" data-id="element-2438">
                Recent Activity
              </p>
            </div>
            <div className="divide-y divide-gray-50" data-id="element-2439">
              <div className="flex items-center gap-3 px-3 py-2" data-id="element-2440">
                <FileTextIcon className="w-3 h-3 text-carbon-bright flex-shrink-0" data-id="element-2441" />
                <span className="text-[11px] text-carbon-deep font-medium flex-1" data-id="element-2442">
                  CSRD Report Q4 generated
                </span>
                <span className="text-[10px] text-gray-400" data-id="element-2443">
                  ESRS E1 Climate
                </span>
                <span className="text-[10px] text-gray-400 flex items-center gap-1" data-id="element-2444">
                  <ClockIcon className="w-2.5 h-2.5" data-id="element-2445" /> 15m ago
                </span>
              </div>
              <div className="flex items-center gap-3 px-3 py-2" data-id="element-2446">
                <CheckCircle2Icon className="w-3 h-3 text-emerald-500 flex-shrink-0" data-id="element-2447" />
                <span className="text-[11px] text-carbon-deep font-medium flex-1" data-id="element-2448">
                  Scope 2 data imported
                </span>
                <span className="text-[10px] text-gray-400" data-id="element-2449">
                  UMEME utility bills
                </span>
                <span className="text-[10px] text-gray-400 flex items-center gap-1" data-id="element-2450">
                  <ClockIcon className="w-2.5 h-2.5" data-id="element-2451" /> 1h ago
                </span>
              </div>
              <div className="flex items-center gap-3 px-3 py-2" data-id="element-2452">
                <ShieldCheckIcon className="w-3 h-3 text-blue-500 flex-shrink-0" data-id="element-2453" />
                <span className="text-[11px] text-carbon-deep font-medium flex-1" data-id="element-2454">
                  CBAM declaration submitted
                </span>
                <span className="text-[10px] text-gray-400" data-id="element-2455">
                  Cement exports Q3
                </span>
                <span className="text-[10px] text-gray-400 flex items-center gap-1" data-id="element-2456">
                  <ClockIcon className="w-2.5 h-2.5" data-id="element-2457" /> 3h ago
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}
/* ═══════════════════════════════════════════════
   ICON MAP FOR FEATURES
   ═══════════════════════════════════════════════ */
const iconMap: Record<string, ComponentType<{
  className?: string;
}>> = {
  BarChart3Icon,
  FileTextIcon,
  GlobeIcon,
  ShieldCheckIcon,
  ScaleIcon,
  ScanLineIcon
};
/* ═══════════════════════════════════════════════
   DATA
   ═══════════════════════════════════════════════ */
const industries: IndustryInfo[] = [{
  icon: LandmarkIcon,
  name: 'Commercial Banks',
  color: 'bg-blue-600',
  description: 'Core IFRS S1/S2 disclosures, financed emissions (Scope 3 Category 15), and portfolio climate risk reporting for regulated Ugandan banks.'
}, {
  icon: ShieldCheckIcon,
  name: 'Development Finance Institutions',
  color: 'bg-emerald-600',
  description: 'Impact and climate risk reporting aligned to donor requirements, ISSB guidance, and long-horizon project finance portfolios.'
}, {
  icon: BarChart3Icon,
  name: 'Insurance & Reinsurance',
  color: 'bg-sky-600',
  description: 'Underwriting exposure, physical risk hotspots, and IFRS S1/S2 governance disclosures for climate-sensitive portfolios.'
}, {
  icon: DatabaseIcon,
  name: 'Microfinance & SACCOs',
  color: 'bg-indigo-600',
  description: 'Right-sized disclosure workflows, borrower emissions proxies, and simplified data collection for regulated MFIs and SACCOs.'
}, {
  icon: GlobeIcon,
  name: 'Fintech & Digital Lenders',
  color: 'bg-purple-600',
  description: 'Automated data capture across dispersed portfolios with real-time IFRS S2 metrics and climate risk dashboards.'
}, {
  icon: ScaleIcon,
  name: 'Pension Funds & Asset Managers',
  color: 'bg-slate-600',
  description: 'Portfolio-level climate risk, stewardship disclosures, and investor-ready reporting for AUM oversight.'
}];
const features = [{
  icon: 'BarChart3Icon',
  title: 'GHG Protocol Scopes 1-3',
  description: 'Complete greenhouse gas inventory following the GHG Protocol Corporate Standard. Automated Scope 3 calculations across 15 categories with Uganda-specific emission factors.'
}, {
  icon: 'FileTextIcon',
  title: 'IFRS S1 & S2 (ISSB)',
  description: 'Generate sustainability and climate disclosures aligned with IFRS S1 (General) and S2 (Climate). Scenario analysis, transition plans, and physical risk assessments included.'
}, {
  icon: 'GlobeIcon',
  title: 'EU CSRD Compliance',
  description: 'Double materiality assessments, ESRS-aligned reporting, and value chain due diligence. Ready for the European Sustainability Reporting Standards.'
}, {
  icon: 'ScaleIcon',
  title: 'CBAM Reporting',
  description: 'Calculate embedded emissions for goods exported to the EU. Automated CBAM declarations with product-level carbon intensity tracking.'
}, {
  icon: 'ShieldCheckIcon',
  title: 'Uganda Climate Change Act',
  description: 'Comply with the National Climate Change Act 2021 requirements for GHG inventories, climate action plans, and reporting to the Ministry of Water and Environment.'
}, {
  icon: 'ScanLineIcon',
  title: 'Audit-Ready Documentation',
  description: 'Full digital audit trail for NEMA environmental audits. Automated evidence collection, data lineage tracking, and third-party verification support.'
}];
const stats = [{
  value: '$2.4B+',
  label: 'EU-Uganda Trade',
  detail: 'Annual bilateral trade value subject to CBAM and EUDR requirements'
}, {
  value: '150+',
  label: 'Reporting Entities',
  detail: 'Ugandan companies expected to fall under mandatory ESG disclosure by 2027'
}, {
  value: '$47/ton',
  label: 'EU Carbon Price',
  detail: 'CBAM will apply EU carbon pricing to imports from non-pricing jurisdictions'
}, {
  value: '2025',
  label: 'CSRD Deadline',
  detail: 'First wave of EU sustainability reporting standards now in effect'
}];

/* ═══════════════════════════════════════════════
   MAIN PAGE COMPONENT
   ═══════════════════════════════════════════════ */
export function IFRSS1S2Reporting() {
  const statsRef = useRef(null);
  const statsInView = useInView(statsRef, {
    once: true,
    margin: '-60px'
  });
  const impactRef = useRef(null);
  const impactInView = useInView(impactRef, {
    once: true,
    margin: '-80px'
  });
  const industriesRef = useRef(null);
  const industriesInView = useInView(industriesRef, {
    once: true,
    margin: '-60px'
  });
  const featuresRef = useRef(null);
  const featuresInView = useInView(featuresRef, {
    once: true,
    margin: '-80px'
  });
  const platformRef = useRef(null);
  const platformInView = useInView(platformRef, {
    once: true,
    margin: '-60px'
  });
  const ctaRef = useRef(null);
  const ctaInView = useInView(ctaRef, {
    once: true,
    margin: '-60px'
  });
  return <>
      {/* ═══ HERO ═══ */}
      <section className="relative w-full overflow-hidden bg-white" data-id="element-2458">
        <svg className="absolute inset-0 w-full h-full opacity-[0.04]" aria-hidden="true" data-id="element-2459">

          <defs data-id="element-2460">
            <pattern id="ghg-hero-grid" width="50" height="50" patternUnits="userSpaceOnUse" patternTransform="rotate(45)" data-id="element-2461">

              <line x1="0" y1="0" x2="0" y2="50" stroke="#1B4332" strokeWidth="0.5" data-id="element-2462" />

              <line x1="0" y1="0" x2="50" y2="0" stroke="#1B4332" strokeWidth="0.5" data-id="element-2463" />

            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#ghg-hero-grid)" data-id="element-2464" />
        </svg>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28 lg:py-36" data-id="element-2465">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-center" data-id="element-2466">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.7,
            ease: [0.22, 1, 0.36, 1]
          }} data-id="element-2467">

              <p className="text-sm font-semibold uppercase tracking-widest text-carbon-bright mb-4" data-id="element-2468">
                IFRS S1 & S2 Reporting for Ugandan Banks
              </p>
              <h1 className="text-[3.5rem] font-bold text-carbon-deep tracking-tight leading-[1.1]" data-id="element-2469">
                Bank-grade{' '}
                <span className="relative inline-block" data-id="element-2470">
                  <span className="relative z-10" data-id="element-2471">IFRS S1 & S2 Reporting</span>
                  <span className="absolute bottom-1 left-0 right-0 h-[40%] bg-carbon-bright/25 -z-0 rounded-sm" aria-hidden="true" data-id="element-2472" />

                </span>
              </h1>
              <p className="mt-6 text-lg text-gray-500 leading-relaxed max-w-xl" data-id="element-2473">
                Designed for Ugandan banks, DFIs, and insurers, CarbonSafe
                streamlines IFRS S1/S2 disclosures, financed emissions, and
                climate risk reporting. Deliver audit-ready outputs aligned to
                Bank of Uganda expectations, ISSB guidance, and global investor
                scrutiny.
              </p>
              <div className="mt-5 flex flex-wrap gap-3" data-id="element-2474">
                <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border border-[#9aa6af] bg-white text-sm font-medium text-carbon-deep" data-id="element-2475">
                  <span className="w-2 h-2 rounded-full bg-carbon-bright" data-id="element-2476" />
                  IFRS S1/S2
                </span>
                <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border border-[#9aa6af] bg-white text-sm font-medium text-carbon-deep" data-id="element-2477">
                  <span className="w-2 h-2 rounded-full bg-carbon-bright" data-id="element-2478" />
                  PCAF
                </span>
                <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border border-[#9aa6af] bg-white text-sm font-medium text-carbon-deep" data-id="element-2479">
                  <span className="w-2 h-2 rounded-full bg-carbon-bright" data-id="element-2480" />
                  Basel Climate Risk
                </span>
                <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border border-[#9aa6af] bg-white text-sm font-medium text-carbon-deep" data-id="element-2481">
                  <span className="w-2 h-2 rounded-full bg-carbon-bright" data-id="element-2482" />
                  Bank of Uganda
                </span>
              </div>
              <div className="mt-8 flex flex-col sm:flex-row gap-4" data-id="element-2483">
                <a href={MAILTO.ghgDemo} className="inline-flex items-center gap-2 px-7 py-3.5 bg-[#6bcb78] text-white text-base font-semibold rounded-lg hover:bg-carbon-light transition-colors shadow-lg shadow-carbon-bright/20" data-id="element-2484">

                  See CarbonSafe in action
                  <ArrowRightIcon className="w-4 h-4" data-id="element-2485" />
                </a>
                <a href={MAILTO.ghgPlatform} className="inline-flex items-center gap-2 px-7 py-3.5 border border-[#2d3c48] text-carbon-deep text-base font-semibold rounded-lg hover:bg-carbon-bright/5 transition-colors" data-id="element-2486">

                  Explore The Platform
                </a>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.8,
            delay: 0.2,
            ease: [0.22, 1, 0.36, 1]
          }} className="relative" data-id="element-2487">

              <HeroImageComposition data-id="element-2488" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══ STATS ═══ */}
      <section ref={statsRef} className="w-full bg-carbon-deep py-20 sm:py-28" data-id="element-2489">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2490">
          <motion.div className="text-center mb-16" initial={{
          opacity: 0,
          y: 20
        }} animate={statsInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-2491">

            <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight" data-id="element-2492">
              The ESG compliance imperative
            </h2>
            <p className="mt-4 text-lg text-white/50 max-w-2xl mx-auto" data-id="element-2493">
              Global sustainability regulations are reshaping how Ugandan
              enterprises measure, report, and reduce their environmental
              impact.
            </p>
          </motion.div>
          <motion.div className="grid grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8" initial="hidden" animate={statsInView ? 'visible' : 'hidden'} variants={{
          hidden: {},
          visible: {
            transition: {
              staggerChildren: 0.12
            }
          }
        }} data-id="element-2494">

            {stats.map(stat => <motion.div key={stat.label} variants={{
            hidden: {
              opacity: 0,
              y: 30
            },
            visible: {
              opacity: 1,
              y: 0,
              transition: {
                duration: 0.6
              }
            }
          }} data-id="element-2495">

                <AnimatedStat {...stat} data-id="element-2496" />
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* ═══ HOW IT WORKS ═══ */}
      <HowCarbonSafeWorks verticalName="GHG accounting" subtext="From data collection to audit-ready reports, four steps to verified emissions management." steps={[{
      icon: 'BarChart3Icon',
      title: 'Data Collection',
      description: 'Automated ingestion from utility bills, fuel records, supply chain data, and IoT sensors across all operations.'
    }, {
      icon: 'CpuIcon',
      title: 'Emissions Calculation',
      description: 'GHG Protocol-aligned calculations for Scope 1, 2, and 3 using Uganda-specific and IPCC emission factors.'
    }, {
      icon: 'FileTextIcon',
      title: 'Report Generation',
      description: 'Auto-generate IFRS S1/S2, CSRD, CBAM, and GHG Protocol reports with full audit trail.'
    }, {
      icon: 'ShieldCheckIcon',
      title: 'Verification & Filing',
      description: 'Third-party verification support and direct submission to regulatory bodies and ESG rating agencies.'
    }]} data-id="element-2497" />


      {/* ═══ SCOPE BREAKDOWN ═══ */}
      <section ref={impactRef} className="w-full bg-white py-24 sm:py-32" data-id="element-2498">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2499">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-20 items-center" data-id="element-2500">
            <motion.div initial={{
            opacity: 0,
            x: -40
          }} animate={impactInView ? {
            opacity: 1,
            x: 0
          } : {}} transition={{
            duration: 0.7,
            ease: [0.22, 1, 0.36, 1]
          }} data-id="element-2501">

              <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/3zho3j7rbfxjzyaqujg3yf/eabcd6cc-4563-497d-b5b9-3dca6804fbf0/pasted-image.png" alt="Emissions over time by department" className="w-full h-auto rounded-2xl shadow-lg border border-[#9aa6af]" data-id="element-2502" />

            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 40
          }} animate={impactInView ? {
            opacity: 1,
            x: 0
          } : {}} transition={{
            duration: 0.7,
            delay: 0.15,
            ease: [0.22, 1, 0.36, 1]
          }} data-id="element-2503">

              <span className="inline-block text-[11px] font-bold uppercase tracking-widest bg-carbon-bright/10 px-3 py-1 rounded-full mb-6" data-id="element-2504">
                Full-Scope Coverage
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-carbon-deep tracking-tight leading-tight" data-id="element-2505">
                From direct operations to entire value chains
              </h2>
              <p className="mt-6 text-base text-gray-500 leading-relaxed" data-id="element-2506">
                Most Ugandan enterprises only track Scope 1 and 2 emissions —
                missing the 50-80% of their carbon footprint hidden in supply
                chains, logistics, and product use. CarbonSafe's automated Scope
                3 engine maps your entire value chain, from raw material
                extraction to end-of-life disposal.
              </p>
              <div className="mt-10 grid grid-cols-3 gap-6" data-id="element-2507">
                <div data-id="element-2508">
                  <p className="text-3xl sm:text-4xl font-bold text-carbon-bright" data-id="element-2509">
                    15
                  </p>
                  <p className="mt-1 text-sm text-gray-500 font-medium" data-id="element-2510">
                    Scope 3 categories
                  </p>
                </div>
                <div data-id="element-2511">
                  <p className="text-3xl sm:text-4xl font-bold text-carbon-bright" data-id="element-2512">
                    500+
                  </p>
                  <p className="mt-1 text-sm text-gray-500 font-medium" data-id="element-2513">
                    Data sources
                  </p>
                </div>
                <div data-id="element-2514">
                  <p className="text-3xl sm:text-4xl font-bold text-carbon-bright" data-id="element-2515">
                    99.9%
                  </p>
                  <p className="mt-1 text-sm text-gray-500 font-medium" data-id="element-2516">
                    Calculation accuracy
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══ INDUSTRIES WE SERVE ═══ */}
      <section ref={industriesRef} className="w-full bg-gray-50 py-24 sm:py-32" data-id="element-2517">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2518">
          <motion.div className="text-center mb-16" initial={{
          opacity: 0,
          y: 20
        }} animate={industriesInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-2519">

            <span className="inline-block text-[11px] font-bold uppercase tracking-widest bg-carbon-bright/10 px-3 py-1 rounded-full mb-6" data-id="element-2520">
              Who We Serve
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-carbon-deep tracking-tight" data-id="element-2521">
              Financial institutions we serve
            </h2>
            <p className="mt-5 text-lg text-gray-500 max-w-3xl mx-auto" data-id="element-2522">
              Built for Uganda's regulated financial sector — from tier-one
              banks to DFIs and insurers — with disclosure workflows aligned to
              IFRS S1/S2 and local supervisory expectations.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-id="element-2523">
            {industries.map((industry, i) => <IndustryCard key={industry.name} industry={industry} index={i} isInView={industriesInView} data-id="element-2524" />)}
          </div>

          {/* Regulatory Callout */}
          <motion.div className="mt-14" initial={{
          opacity: 0,
          y: 20
        }} animate={industriesInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          delay: 0.6,
          duration: 0.6,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-2525">

            <div className="p-8 rounded-2xl bg-carbon-deep text-white" data-id="element-2526">
              <div className="flex items-start gap-5" data-id="element-2527">
                <div className="w-12 h-12 rounded-xl bg-carbon-bright/20 flex items-center justify-center flex-shrink-0" data-id="element-2528">
                  <ShieldCheckIcon className="w-6 h-6 text-carbon-bright" data-id="element-2529" />
                </div>
                <div className="flex-1" data-id="element-2530">
                  <h3 className="text-lg font-bold" data-id="element-2531">
                    Uganda's Climate Regulatory Landscape
                  </h3>
                  <p className="mt-2 text-sm text-white/60 leading-relaxed" data-id="element-2532">
                    The National Climate Change Act 2021 mandates GHG
                    inventories for designated entities. NEMA's Environmental
                    Audit Regulations require annual environmental audits for
                    manufacturing facilities. The Uganda Green Growth
                    Development Strategy sets economy-wide emission reduction
                    targets. A carbon tax framework is under active discussion
                    at the Uganda Revenue Authority, expected to affect
                    industrial emitters by 2027.
                  </p>
                  <a href={MAILTO.complianceGuide} className="inline-flex items-center gap-2 mt-4 text-sm font-semibold text-carbon-bright hover:text-carbon-light transition-colors" data-id="element-2533">

                    <DownloadIcon className="w-4 h-4" data-id="element-2534" />
                    Download Compliance Guide
                  </a>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ═══ FEATURES ═══ */}
      <section ref={featuresRef} className="w-full bg-white py-24 sm:py-32" data-id="element-2535">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2536">
          <motion.div className="text-center mb-16" initial={{
          opacity: 0,
          y: 20
        }} animate={featuresInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-2537">

            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-carbon-deep tracking-tight" data-id="element-2538">
              Comprehensive ESG reporting for{' '}
              <span data-id="element-2539">every framework</span>
            </h2>
            <p className="mt-5 text-lg text-gray-500 max-w-2xl mx-auto" data-id="element-2540">
              One platform that speaks the language of every major
              sustainability reporting standard and Ugandan regulation.
            </p>
          </motion.div>

          <motion.div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" initial="hidden" animate={featuresInView ? 'visible' : 'hidden'} variants={{
          hidden: {},
          visible: {
            transition: {
              staggerChildren: 0.1
            }
          }
        }} data-id="element-2541">

            {features.map(feature => {
            const IconComponent = iconMap[feature.icon];
            return <motion.article key={feature.title} className="p-8 rounded-2xl border border-[#9aa6af] bg-gray-50" variants={{
              hidden: {
                opacity: 0,
                y: 40
              },
              visible: {
                opacity: 1,
                y: 0,
                transition: {
                  duration: 0.6,
                  ease: [0.22, 1, 0.36, 1]
                }
              }
            }} data-id="element-2542">

                  <div className="w-14 h-14 rounded-xl bg-carbon-bright/10 flex items-center justify-center mb-6" data-id="element-2543">
                    {IconComponent && <IconComponent className="w-7 h-7 text-carbon-bright" data-id="element-2544" />}
                  </div>
                  <h3 className="text-lg font-bold text-carbon-deep mb-2" data-id="element-2545">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-gray-500 leading-relaxed" data-id="element-2546">
                    {feature.description}
                  </p>
                </motion.article>;
          })}
          </motion.div>
        </div>
      </section>

      {/* ═══ PLATFORM PREVIEW ═══ */}
      <section ref={platformRef} className="w-full bg-gray-50 py-24 sm:py-32" data-id="element-2547">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2548">
          <motion.div className="text-center mb-16" initial={{
          opacity: 0,
          y: 20
        }} animate={platformInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-2549">

            <span className="inline-block text-[11px] font-bold uppercase tracking-widest bg-carbon-bright/10 px-3 py-1 rounded-full mb-6" data-id="element-2550">
              Platform Preview
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-carbon-deep tracking-tight" data-id="element-2551">
              GHG accounting and ESG reporting in{' '}
              <span data-id="element-2552">one dashboard</span>
            </h2>
            <p className="mt-5 text-lg text-gray-500 max-w-2xl mx-auto" data-id="element-2553">
              From emissions data collection to CSRD-compliant reports: measure,
              manage, and disclose with confidence.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 60
        }} animate={platformInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.8,
          delay: 0.2,
          ease: [0.22, 1, 0.36, 1]
        }} className="max-w-5xl mx-auto" data-id="element-2554">

            <GHGDashboardMockup data-id="element-2555" />

          </motion.div>
        </div>
      </section>

      {/* ═══ CTA ═══ */}
      <section ref={ctaRef} className="w-full py-24 sm:py-32 bg-white" data-id="element-2556">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2557">
          <motion.div className="relative overflow-hidden rounded-3xl px-8 py-16 sm:px-16 sm:py-20 text-center" style={{
          background: 'linear-gradient(135deg, #2D6A4F 0%, #1B4332 100%)'
        }} initial={{
          opacity: 0,
          y: 40
        }} animate={ctaInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.7,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-2558">

            <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full opacity-10" style={{
            background: 'radial-gradient(circle, #52B788, transparent)'
          }} aria-hidden="true" data-id="element-2559" />

            <div className="absolute -bottom-16 -left-16 w-48 h-48 rounded-full opacity-10" style={{
            background: 'radial-gradient(circle, #6BCB77, transparent)'
          }} aria-hidden="true" data-id="element-2560" />

            <h2 className="relative text-3xl sm:text-4xl lg:text-5xl font-semibold text-white tracking-tight leading-tight max-w-3xl mx-auto" data-id="element-2561">
              Every ton measured.
              <br data-id="element-2562" />
              <span data-id="element-2563">Every report trusted.</span>
            </h2>
            <p className="relative mt-6 text-lg text-white/60 max-w-xl mx-auto" data-id="element-2564">
              Join Uganda's leading enterprises using CarbonSafe to automate GHG
              accounting and meet global ESG reporting standards.
            </p>
            <div className="relative mt-10 flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-2565">
              <a href={MAILTO.ghgDemo} className="inline-flex items-center gap-2.5 px-8 py-4 bg-[#6bcb78] text-white text-base font-semibold rounded-full hover:bg-carbon-light transition-colors shadow-lg shadow-black/20" data-id="element-2566">

                Start Your ESG Journey
                <ArrowRightIcon className="w-4 h-4" data-id="element-2567" />
              </a>
          </div>
          </motion.div>
        </div>
      </section>

      <Footer data-id="element-2568" />
    </>;
}